// 消息气泡 —— 三种变体：self / peer / system
// 微信式左右对齐，黑白专业风替换微信的绿色

import { Avatar } from "./Avatar";
import { ExpandableMedia } from "./ExpandableMedia";
import { VoicePlayer } from "./VoicePlayer";
import type { ChatAttachment } from "../lib/use-room";

export interface BubbleMessage {
  id: string;
  device: string;
  text: string;
  ts: number;
  fromSelf?: boolean;
  isAgent?: boolean;
  isSystem?: boolean;
  /** F3b: 此消息其实是审批请求，MessageList 应渲染 ApprovalCard 而不是普通气泡 */
  approvalRequest?: {
    invocation_id: string;
    caller_user_id: string;
    room_id: string;
    adapter_id: string;
    prompt: string;
    prompt_sha256: string;
    expires_at: number;
  };
  /** D5: 聚合后的 agent invocation，MessageList 应渲染 TerminalBlock */
  invocationId?: string;
  isComplete?: boolean;
  /** T3 §3.4 EOF reason —— TerminalBlock 渲染语义化状态文案 */
  eofReason?: string;
  /** T3 §3.4 重连后服务器告知此 invocation 已不再 active，UI 提示"重新发起" */
  stale?: boolean;
  /** T3 §3.5 服务端 ack 的 cols/rows */
  resizeAck?: { cols: number; rows: number };
  attachment?: ChatAttachment;
}

interface MessageBubbleProps {
  msg: BubbleMessage;
  /** 同 sender 连续消息时折叠 sender 名与头像 */
  compact?: boolean;
}

export function MessageBubble({ msg, compact }: MessageBubbleProps) {
  if (msg.isSystem) {
    return (
      <li className="bubble bubble--system">
        <span>{msg.text}</span>
      </li>
    );
  }

  const side: "self" | "peer" = msg.fromSelf ? "self" : "peer";
  return (
    <li className={`bubble bubble--${side}${compact ? " bubble--compact" : ""}`}>
      {side === "peer" && !compact && <Avatar seed={msg.device} size={32} />}
      {side === "peer" && compact && <span className="bubble__avatar-spacer" />}
      <div className="bubble__col">
        {!compact && (
          <header className="bubble__meta">
            {side === "peer" && (
              <span className={msg.isAgent ? "bubble__sender bubble__sender--agent" : "bubble__sender"}>
                {msg.device}
              </span>
            )}
            <time className="bubble__ts">{formatTime(msg.ts)}</time>
          </header>
        )}
        <div className="bubble__body">
          {msg.attachment ? <AttachmentView attachment={msg.attachment} /> : msg.text}
          {msg.attachment && msg.text && <p className="bubble__caption">{msg.text}</p>}
        </div>
      </div>
    </li>
  );
}

function AttachmentView({ attachment }: { attachment: ChatAttachment }) {
  if (attachment.kind === "image") {
    return (
      <ExpandableMedia
        title={attachment.name}
        inlineHeight={300}
        className="attach attach--image"
        actions={
          <a
            className="exp-media__action"
            href={attachment.dataUrl}
            download={attachment.name}
            onClick={(e) => e.stopPropagation()}
          >
            下载
          </a>
        }
      >
        <img
          src={attachment.dataUrl}
          alt={attachment.name}
          className="attach__image"
        />
      </ExpandableMedia>
    );
  }
  if (attachment.kind === "audio") {
    return (
      <div className="attach attach--audio">
        <VoicePlayer src={attachment.dataUrl} name={attachment.name} />
      </div>
    );
  }
  return (
    <ExpandableMedia
      title={attachment.name}
      inlineHeight={360}
      className="attach attach--html"
      actions={
        <>
          <a
            className="exp-media__action"
            href={attachment.dataUrl}
            target="_blank"
            rel="noreferrer"
            onClick={(e) => e.stopPropagation()}
          >
            打开
          </a>
          <a
            className="exp-media__action"
            href={attachment.dataUrl}
            download={attachment.name}
            onClick={(e) => e.stopPropagation()}
          >
            下载
          </a>
        </>
      }
    >
      <iframe
        title={attachment.name}
        src={attachment.dataUrl}
        sandbox=""
        className="attach__iframe"
      />
    </ExpandableMedia>
  );
}

function formatTime(ts: number): string {
  const d = new Date(ts);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
